import { useMotionValue, useTransform } from "framer-motion"
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef } from "react"
import { useLocale } from "contexts/Locale"
import { storeCustomEvent, waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { logmine } from "./utils"
import { STEPS } from "./constants"
import { useEnv } from "contexts/Env"
import { useUser } from "contexts/User"
import { getPriceDatas } from "components/price/utils"

function updateLoadingQ(loadingQ, index, state) {
  let q = loadingQ.split("")
  q[index] = state
  return q.join("")
}

export const findById = (id, list, key) => {
  return !!list && !!key ? list.find(v => v[key] === id) : null
}

export const WatchVariationsContextProvider = ({ children, Ctx, kill, ...props }) => {
  const firstload = useMotionValue(true)
  const rfroot = useRef(null)
  const ctxprops = props.ctxprops || null
  const mode = props.mode || null
  const dict = useMotionValue(null)
  const layout = props.layout || {}
  const datapatch = props.datapatch || null
  const tint = useMotionValue(props.tint || null)
  const popin = useMotionValue(props.popin || null)
  const entry = useMotionValue(props.entry || null)
  const reset = useMotionValue(props.reset || null)
  const route = useMotionValue(props.route || null)
  const family = useMotionValue(props.family || null)
  const familyrange = props.families || []
  const category = useMotionValue(null)
  const flagship = useMotionValue(props.flagship || null)
  const variations = (props.variations || "").split(",")
  const varparams = props.varparams
  const varexpanded = useMotionValue(props.varexpanded || false)
  const stepindex = useMotionValue(props.stepindex || -1)
  const steps = useMotionValue(props.steps || null)
  const active = useMotionValue(props.active || null)
  //  const wactive = useMotionValue(null)
  const newactive = useMotionValue(props.active || null)
  //  const wnewactive = useMotionValue(null)
  const loadingQ = useMotionValue("00000")
  const busy = useMotionValue(false)
  const newstepindex = useMotionValue(props.newstepindex || 0)
  const collection = useMotionValue(null)
  //  const labels = useMotionValue(props.labels||null)
  const config = useMotionValue(null)
  //  const rules = buildWorldsRules(props.worlds)
  const world = useMotionValue(-1)
  //  const wupdate = useMotionValue(false)
  const coverReady = useMotionValue(false)
  const fromui = useMotionValue(true)
  const hashing = useMotionValue(false)
  const moving = useMotionValue(false)
  const cta = useMotionValue(null)
  const rtl = process.browser && document.querySelector("html").getAttribute("dir") === "rtl"
  const winWidth = useMotionValue(process.browser ? document.documentElement.clientWidth : 0)
  const winHeight = useMotionValue(process.browser ? document.documentElement.clientHeight : 0)
  const footeredh = useMotionValue(0)
  const sticky = useMotionValue(false)
  const ended = useMotionValue(false)
  const last = useMotionValue(null)
  const highlight = useMotionValue(false)
  const glow = useMotionValue(0)
  const wrready = useMotionValue(false)
  const slready = useMotionValue(true)
  const catready = useMotionValue(false)
  const swapping = useMotionValue(null)
  const toggle = useMotionValue(1)
  const env = useEnv()
  const locale = useLocale()
  const { countryCode } = useUser()
  const incard = props.incard
  const pricelist = props.pricelist

  let waStarted = useRef(true).current

  const gready = useTransform([wrready, slready, catready], ([...args]) => {
    return args.every(v => v)
  })

  function wa(data = {}, completed, same, incard) {
    if (!process.browser) return
    if (completed && !incard) {
      storeCustomEvent({ eventName: "configuratorCompleted" })
      if (!same) return
    }
    const m = getModel(active.get())
    if (!m) return

    let split = window.location.pathname.slice(1).split("/")
    if (locale.current.codes.www !== locale.defaultLocale.codes.www) split.splice(0, 1)
    let [siteSection, siteSubSection] = split
    siteSubSection = siteSubSection || ""

    let eventName = completed && same ? [] : ["configuratorStepViews"];
    if (waStarted) eventName.push("configuratorStarted", "viewVariationsCTA");

    let obj = {
      eventName: eventName,
      eventType: "Page Load",
      pageName: `watches:${m.familyCode}:${m.uid}:step:${step2wa()}`,
      products: [{
        productCategory: m.familyCode,
        productModel: m.nameCode,
        productID: m.uid,
        productQuantity: "1",
        currency: m.currency,
        productPrice: m.price
      }],
      ...data,
    }
    /*
        const lastElement = global.digitalDataLayer[global.digitalDataLayer.length - 1] ?? { data: {} }
        const _data = lastElement.data
        if (_data?.page) {
          const page = JSON.parse(JSON.stringify(_data.page))
          page.pageURL = global.location.href
          obj.page = page
        }
    */
    if (!completed)
      obj.configurator = {
        step: step2wa(),
      }

    if (incard && completed) {
      obj.eventName = "configuratorCompleted"
      obj.eventType = "Click"
    }

    triggerWa()

    function step2wa() {
      return STEPS[stepindex.get()]
    }

    async function triggerWa() {
      if (!m.currency || !m.price) {
        const { price, currency } = await getPriceDatas({ rmc: m.uid, countryCode, locale, env })
        m.currency = currency
        m.price = price
        obj.products[0].currency = currency
        obj.products[0].productPrice = price
      }
      waTriggerEvent(obj)
    }
  }

  const initQ = useCallback(
    v => {
      //      logmine("Callback", "Context", "initQ", v)
      loadingQ.set(updateLoadingQ(loadingQ.get(), v, 1))
    },
    [loadingQ]
  )

  const loadQ = useCallback(
    v => {
      //      logmine("Callback", "Context", "loadQ", v)
      loadingQ.set(updateLoadingQ(loadingQ.get(), v, 0))
    },
    [loadingQ]
  )

  const getModel = props.getModel ?? (uid => {
    if (!collection.get() || !uid) return null
    return collection.get().find(v => v.uid === uid)
  })

  function updateModel(rmc, { currency, price }) {
    const m = getModel(rmc)
    if (!m) return
    m.currency = currency
    m.price = price
  }

  const wactive = useTransform([active, collection], ([active, collection]) => {
    return getModel(active)
  })

  const wentry = useTransform([entry, collection], ([entry, collection]) => {
    return getModel(entry)
  })

  const wnewactive = useTransform([newactive, collection], ([newactive, collection]) => {
    return getModel(newactive)
  })

  const onResize = useCallback(
    e => {
      //      logmine("Callback", "Context", "onResize")
      winWidth.set(document.documentElement.clientWidth)
      winHeight.set(document.documentElement.clientHeight)
    },
    [winWidth, winHeight]
  )

  useEffect(() => {
    //    logmine("useEffect", "Context", "onResize")
    if (!process.browser) return
    window.addEventListener("resize", onResize, false)
    window.addEventListener("orientationchange", onResize, false)

    return () => {
      window.removeEventListener("resize", onResize, false)
      window.removeEventListener("orientationchange", onResize, false)
    }
  }, [onResize])

  function onStepindex(v) {
    if (v < 1) return (waStarted = true)
    wa()
    waStarted = false
  }

  useEffect(() => mvSubscribe("Context stepindex", stepindex, onStepindex, false), [])

  return (
    <Ctx.Provider
      value={{
        firstload,
        rfroot,
        ctxprops,
        mode,
        layout,
        datapatch,
        tint,
        popin,
        entry,
        reset,
        route,
        family,
        familyrange,
        category,
        flagship,
        variations,
        varparams,
        varexpanded,
        stepindex,
        steps,
        active,
        wactive,
        wentry,
        newactive,
        wnewactive,
        //        shadow,
        loadingQ,
        busy,
        newstepindex,
        collection,
        //        dict,
        //        labels,
        //        config,
        //        rules,
        world,
        //        wupdate,
        coverReady,
        fromui,
        hashing,
        rtl,
        moving,
        cta,
        winWidth,
        winHeight,
        footeredh,
        sticky,
        //        focus,
        initQ,
        loadQ,
        ended,
        last,
        highlight,
        glow,
        getModel,
        wrready,
        slready,
        catready,
        gready,
        swapping,
        wa,
        toggle,
        kill,
        incard,
        pricelist,
        updateModel
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export const useWatchVariations = ctx => useContext(ctx)

export const mvSubscribe = (ctx, mv, changeFunc, run = true) => {
  const unsub = mv.onChange(v => {
    //    logmine("onChange", ctx, LOG.get(mv) || "unknown", v)
    changeFunc(v)
  })
  if (run) {
    //    logmine("onChange", ctx, LOG.get(mv) || "unknown", mv.get())
    changeFunc(mv.get())
  }
  return unsub
}

export const fromCollection = list => uid => {
  //  logmine("Func", "Context", "fromCollection", uid)
  if (!list || !uid) return null
  return list.find(v => v.uid === uid)
}
